import type { Cycle } from "./types";
import { GRADING_LEVELS } from "./types";

export const TRAIN_DRIVER_V1_CYCLE: Cycle = {
  id: "train-driver-v1",
  name: "Train Driver v1",
  gradingLevels: GRADING_LEVELS,
  units: [
    {
      id: "u1",
      title: "UNIT 1 - Prepare for duty",
      elements: [
        {
          id: "1.2",
          title: "Element 1.2 Report for duty using approved methods",
          demonstrate: [
            "a. Attend for duty at the correct time and location, using the approved method and signing the statement of readiness for duty",
            "b. Check with appropriate person to clarify any alterations to work diagram and obtain any amendments as necessary",
            "c. Review work diagram, confirming train formation, departure locations and times, along with traction and route competence for the day's work",
            "d. Carry your ID card and all necessary PPE, maintain it in good order and wear it when required",
            "e. Read late and new notice case items, clarify information where necessary, noting any items relevant to work schedule",
            "f. Read and note relevant information from other notice cases as appropriate",
            "g. Request any new or replacement equipment or publications from the appropriate manager",
            "h. Obtain, acknowledge and read weekly and periodical operating notices, carrying them for reference",
            "i. Obtain, acknowledge, read and update publications as necessary",
            "j. Carry out driver in charge duties where required at an unsupervised location",
          ],
          explain: [
            "1. Approved methods for reporting for duty at your location",
            "2. Time allowance for signing on and actions to take during that time",
            "3. Different types of alteration sheets and how an amendment to work is indicated",
            "4. Personal equipment and publications to be carried whilst on duty and action if they become lost or damaged (including PPE)",
            "5. Reason for rules, regulations and instructions within the rail industry",
            "6. Names of the notice cases at a depot and typical information contained in each case",
            "7. Who to contact to clarify information regarding published notices",
            "8. Sections of the weekly operating notice and information contained in each section",
            "9. When to sign for weekly and periodical operating notices",
            "10. The evacuation and accident reporting procedures",
            "11. Requirements when carrying out driver in charge duties",
          ],
        },
      ],
    },
    {
      id: "u2",
      title: "UNIT 2 - Communicate effectively",
      elements: [
        {
          id: "2.1",
          title: "Element 2.1 Use phonetic alphabet and communication protocols",
          demonstrate: [
            "a. Commence communication by clearly stating your identity and confirming that the correct person has been contacted",
            "b. Clearly state or establish the reasons for the communication",
            "c. Relay verbal information in an accurate, clear and concise manner, in line with the company and rule book requirements",
            "d. Use the phonetic alphabet (codes and numbering) and other verbal communication protocols to relay information as appropriate",
            "e. Listen carefully to all messages and then repeat back information to obtain a clear understanding of any action, reasons or operational restrictions to be applied",
            "f. Switch off all personal mobile phones and portable media players (PMP's) when in the driving cab or when on or near the line",
            "g. Only use a mobile phone in a driving cab in emergency situations or from a 'position of safety'",
            "h. In an emergency situation when making a call, clearly identify that it is an 'emergency call'",
          ],
          explain: [
            "1. Reason for clearly identifying yourself and establishing you are speaking to the correct person",
            "2. Phonetic alphabet and reason for its use",
            "3. Key words and phrases to be used or avoided when communicating safety related information",
            "4. Reason for repeating safety related messages when required",
            "5. Action if any message is not clearly understood",
            "6. The Chiltern Railways policy for use of mobile phone(s) and Portable Media Players in driving cabs and when on or near the line",
            "7. Reasons for asking others to switch off mobile phone(s), pagers and PMP's whilst in the driving cab",
            "8. Process for reporting defective or inoperable communication equipment",
            "9. Circumstances that warrant initiating an emergency call on the cab radio",
          ],
        },
        {
          id: "2.2",
          title: "Element 2.2 Use in-cab and lineside communication protocols",
          demonstrate: [
            "a. Use cab radio and lineside communication equipment in line with operational requirements and then only when it is safe to do so",
            "b. Use appropriate equipment to communicate all relevant information to the Train Manager/passengers in a timely manner",
            "c. Respond appropriately to cab radio emergency calls and stop messages appropriately",
            "d. Respond to routine cab radio calls when it is safe to do so",
          ],
          explain: [
            "1. Different types of cab radios used on Chiltern Railways trains",
            "2. Action required to register and de-register GSM-R cab radio system",
            "3. Functions of the cab radio communication system(s) and when they must be used",
            "4. Different types of lineside communication equipment, how they are identified and used",
            "5. How to identify lineside and signal post telephones and those with limited clearance",
            "6. Action if required to use a telephone identified with limited clearance",
            "7. Timescales for contacting the signaller when detained at a signal with in-cab radio and when within a radio area",
            "8. How to obtain accurate information regarding train delay and service alterations",
            "9. Action required to respond effectively and safely to an emergency call",
          ],
        },
        {
          id: "2.3",
          title: "Element 2.3 Communicate using written methods and procedures",
          demonstrate: [
            "a. Report any significant information in writing accurately, legibly and completely",
            "b. Use correct and appropriate forms when reporting significant information",
            "c. Submit reports in a timely manner prior to leaving duty, or sooner if requested",
            "d. Use appropriate methods to pass on reports to operations control and send a copy to the appropriate manager",
          ],
          explain: [
            "1. Typical information that must be reported prior to leaving duty",
            "2. Typical reasons for submitting a drivers' report",
            "3. Reason for submitting reports prior to leaving duty",
            "4. Different forms that are to be used and where to obtain them",
            "5. Method used to submit drivers' reports at supervised and unsupervised depots",
            "6. Scope of events covered by an RT3185 form",
          ],
        },
      ],
    },
    {
      id: "u3",
      title: "UNIT 3 - Prepare trains for service",
      elements: [
        {
          id: "3.1",
          title: "Element 3.1 Apply personal safety requirements on the railway infrastructure",
          demonstrate: [
            "a. Wear authorised high visibility clothing and sturdy footwear when on or near the line, carrying a hand lamp when required",
            "b. Use authorised walking routes when walking to and from a train or within a yard or depot",
            "c. Ensure personal safety at all times when using lineside equipment",
            "d. Cross the line using approved methods",
            "e. Gain access to the lineside using approved methods and only when required by your duties",
            "f. Walk on or near the line in line with the rules and local instructions",
            "g. Respond appropriately to lineside indications or signs",
            "h. Take appropriate action if a train approaches or train warning horn sounds",
            "i. Act safely in accordance with AC or DC electrified line instructions",
          ],
          explain: [
            "1. Core requirements for personal track safety",
            "2. PPE/equipment likely to be carried when on or near the line",
            "3. Core additional requirements for personal track safety on AC electrified lines",
            "4. Core additional requirements for personal track safety on DC electrified lines (including 3rd and 4th rail systems)",
            "5. Location of authorised walking route information",
            "6. Authorised walking routes to stations, stabling points, depots and sidings that are within a driver's route competence",
            "7. Action if a hazard is identified on a walking route",
            "8. Reasons a driver may need to be on or near the line and the potential hazards",
            "9. Hazards involved in walking and crossing the line and how to avoid them",
            "10. Action to take if you observe a person acting in an unsafe manner or violating rules",
            "11. Information to give and receive when making an emergency call",
            "12. Safety requirements when going track side to change ends",
            "13. How to get traction current switched off in an emergency",
            "14. Action required to maintain and certify route competence",
          ],
        },
        {
          id: "3.2",
          title: "Element 3.2 Prepare and mobilise trains",
          demonstrate: [
            "a. Enter and exit train in a safe manner appropriate to the class of traction concerned",
            "b. Ensure train formation meets service requirements and train preparation is completed and signed off",
            "c. Prepare and mobilise train in line with checklist requirements for train type and within allocated time",
            "d. Conduct brake tests in line with train type, liaising with Train Manager where required",
            "e. Conduct door tests in line with train type, liaising with Train Manager where required",
            "f. Use appropriate fault guide during preparation to accurately and promptly identify and rectify train faults",
          ],
          explain: [
            "1. Action if train formation differs from service requirements",
            "2. Preparation and mobilisation times for each train type",
            "3. Potential safety hazards when entering and exiting a train",
            "4. Location of key equipment on trains and significant differences between the trains you have to operate",
            "5. Safety precautions required when preparing a train within a depot, siding, station or stabling point",
            "6. Reference documents which provide information required for preparation, mobilisation, fault finding and rectification",
            "7. Key success/failure indicators when setting up and testing cab radio systems",
            "8. Purpose, location and use of safety and emergency equipment contained on trains",
            "9. Key success/failure indicators when conducting door tests",
            "10. Key success/failure indicators when testing safety systems and equipment",
            "11. Key success/failure indicators when undertaking personal brake tests",
            "12. Common faults that prevent a train entering service",
            "13. Procedure for recording and reporting train faults",
          ],
        },
        {
          id: "3.3",
          title: "Element 3.3 Drive trains within depots, yards and sidings",
          demonstrate: [
            "a. Clarify and confirm the scope of train movement with the person in charge of movements prior to commencing move",
            "b. Move trains in response to hand, audible or fixed signals as appropriate and always drive from the leading cab, unless otherwise authorised",
            "c. Comply with depot working arrangements when moving a train into or out of a shed",
            "d. Accelerate smoothly, taking account of speed limits, railhead conditions and potential rail contamination",
            "e. Check that all facing/trailing points or derailers are set correctly before passing over them and operate safely any hand points as necessary",
            "f. Control train using knowledge and experience of the location to safely anticipate any route risks on the line ahead",
            "g. Stop train immediately when person controlling move goes out of sight",
            "h. When moving a train using radio communication to control movements, stop the train if communication is lost or is not continuous",
            "i. Reduce train speed to walking pace at least one coach length from the planned stopping point and stop train at the designated stopping point",
            "j. Use DRA when detained at depot stop signals or stop boards",
            "k. When shunting, maintain a good lookout for trains which may be foul or for any potential conflicting movements",
          ],
          explain: [
            "1. Different signals and methods of communication used to control train movements by day and night and during poor visibility",
            "2. Types of fixed signals used to control train movements within and exiting depots and sidings",
            "3. Track layout, route characteristics, local instructions and protection arrangements relevant to the depot/siding you work within",
            "4. Typical hazards when driving trains within depots",
            "5. SPAD and other operational risks within depots and sidings and the actions a driver can take to avoid them",
            "6. Typical hazards when washing trains",
            "7. Use of warning horn or depot whistle within depots",
            "8. Requirements for driving a train from other than the leading cab within depots",
            "9. Instructions when delayed at a signal in a depot",
            "10. Front and rear train illumination requirements within depots and sidings",
            "11. Bell and or buzzer codes used for moving trains within depots",
          ],
        },
        {
          id: "3.4",
          title: "Element 3.4 Couple and uncouple trains",
          demonstrate: [
            "a. Reach a clear understanding with all relevant staff that it is safe to couple or uncouple",
            "b. Stop train at 6ft and 2ft from other vehicles before coupling",
            "c. Check to ensure any coupling, buffers or connecting block covers are positioned correctly or are undamaged",
            "d. When coupling with a locomotive, place brake controller to emergency before giving permission for any person to go between vehicles",
            "e. Check that other train/vehicle is secure before coupling or uncoupling",
            "f. Perform coupling with rolling stock fitted with BSI couplers at a safe speed that avoids heavy impacts",
            "g. Perform coupling with rolling stock fitted with conventional draw-gear at a safe speed and only when the shunter (where applicable) is seen to be clear of the train",
            "h. Conduct a 'pull-away' test",
            "i. Set correct lights and ensure cabs are secured/configured correctly",
            "j. When uncoupling, ensure moves travel no further than the 60cm/2ft and where this needs to be exceeded, appropriate permission is obtained",
            "k. Whenever possible, seek to rectify problems arising during coupling/uncoupling",
            "l. When changing the configuration of a train, ensure a brake continuity test is carried out successfully before proceeding",
          ],
          explain: [
            "1. Actions required to couple and uncouple each traction type",
            "2. Reason for reaching a clear understanding prior to any movement",
            "3. Reasons for stopping at the prescribed distances prior to coupling",
            "4. Actions prior to coupling and or uncoupling",
            "5. The correct position of buffers when buckeye couplings on loco hauled stock are raised or lowered",
            "6. Reason for pressing the couple or uncouple button for a minimum of 5 seconds",
            "7. Actions necessary immediately after coupling/uncoupling",
            "8. Hand signals used by the person controlling the move (day and night)",
            "9. Safety requirements when coupling/uncoupling with customers on a train",
            "10. Reason for conducting a 'pull away' test",
            "11. Typical problems that may arise during coupling/uncoupling and action required to rectify them",
            "12. Additional considerations when attaching to a failed train",
            "13. Reasons for carrying out a brake continuity test",
            "14. Reason for checking the brake cylinder pressure on the other unit after coupling of BSI equipped train types has been completed",
          ],
        },
        {
          id: "3.5",
          title: "Element 3.5 Dispose and stable trains",
          demonstrate: [
            "a. Leave trains clear of all other lines or movements when stabled",
            "b. Stable trains as per the instructions given by the person in charge of the movement",
            "c. Dispose or stable train using approved checklist",
            "d. Secure cab doors to prevent unauthorised access in line with train and cab security requirements",
            "e. Where required, report position of train to the appropriate manager/supervisor",
          ],
          explain: [
            "1. Reference documents that provide train disposal/stabling information",
            "2. Appropriate action to take if a train has to be left foul of another line",
            "3. Potential hazards when exiting a train and actions a driver can take to prevent accidents",
            "4. Timescales allowed for disposal and stabling of the train",
            "5. Action required to secure a train",
            "6. Action required to ensure train security is maintained",
            "7. Actions if appropriate lighting cannot be displayed",
            "8. The process for recording and reporting train faults",
          ],
        },
      ],
    },
    {
      id: "u4",
      title: "UNIT 4 - Operate trains in service",
      elements: [
        {
          id: "4.1",
          title: "Element 4.1 Start trains",
          demonstrate: [
            "a. On DOO(P) trains, where available, ensure the PIS is set up and the appropriate train announcements are made",
            "b. Monitor train departure and dwell times on trains to ensure the train departs promptly",
            "c. On trains dispatched by the driver, follow authorised train dispatch procedures according to the relevant instructions for the location and type of train worked",
            "d. Check the correct signal is cleared",
            "e. Where applicable ensure no obstructions are visible in the 'dispatch corridor' before commencing movement",
            "f. Only reset the DRA after confirming signal is clear",
            "g. When working with a Train Manager, repeat bell/buzzer codes",
            "h. Where fitted, use hill start and sand in line with instructions and railhead conditions",
            "i. Be vigilant when starting from beyond a signal, on a cautionary aspect or on the authority of a position light signal",
            "j. Accelerate smoothly and consistently in line with railhead conditions, train type and previous signal aspect",
            "k. Obtain permission from signaller before making any movement not controlled by signals",
            "l. If asked to carry out an unusual move, ensure requirements are clarified and understood before and move takes place",
          ],
          explain: [
            "1. Core types of signals used on Chiltern Railways routes",
            "2. Types of signalling systems and principles used on Chiltern Railways routes",
            "3. Chiltern Railways professional driving considerations in relation to starting trains",
            "4. Different methods of train dispatch used at each station on the route",
            "5. When a driver will be required to make announcements prior to starting",
            "6. Action if traction interlock cannot be achieved",
            "7. Action if a train ready to start is received before signal is showing a proceed aspect",
            "8. Action if DOO(P) dispatch equipment, (CCTV, CD/RA or mirrors) is defective",
            "9. Circumstances that may lead to a signal being obscured when starting and associated actions",
            "10. Action if delayed at different signal types and when starting signal and associated equipment is defective",
            "11. Signals that have the potential for a 'Starting against Signal' (SAS) or 'Starting on Yellow' (SoY) SPAD and drivers actions to assist in prevention",
            "12. Purpose of the DRA and when it should be set/reset",
            "13. Bell and or buzzer codes used to start/move a train and alternative methods if inoperative",
            "14. Authority people require to travel in driving cabs and the in cab etiquette to comply with requirements",
            "15. Stations that use CD & RA indicators to dispatch trains",
          ],
        },
        {
          id: "4.2",
          title: "Element 4.2 Drive trains",
          demonstrate: [
            "a. Operate the train controls using appropriate techniques",
            "b. Use route knowledge and related risk information to anticipate the safest means of controlling the train for the prevailing conditions",
            "c. Maintain train timings and, wherever possible, allow for an appropriate period of coasting prior to braking",
            "d. When following other trains, control train speed to avoid repeated cancellations of the AWS",
            "e. Respond appropriately to in cab warning indications, instrument readings, restrictive signals, junction indicators and signal reversions",
            "f. Ensure speed restrictions are adhered to over the full length of the train",
            "g. Maintain personal cab discipline and concentration throughout the journey",
            "h. Conduct running brake tests when required during normal and adverse/severe weather conditions",
            "i. Reduce the train speed as necessary during adverse weather or poor rail conditions to control the train safely",
            "j. Promptly report any events that affects the progress of the train to the signaller",
            "k. Use warning horn when required and respond appropriately to people on the line",
            "l. Respond appropriately to lineside signs",
          ],
          explain: [
            "1. Core route features over routes worked",
            "2. Chiltern Railways Professional driving considerations in relation to driving trains",
            "3. Types of route indications used",
            "4. Warning indications that can be received in a driving cab",
            "5. Cab discipline requirements and potential distractions along with the action a driver needs to take to maintain cab discipline",
            "6. Actions if aspect of last signal cannot be recalled",
            "7. Situations where DRA can be used on the move",
            "8. Signs relating to train operations on the lineside and drivers action relating to each",
            "9. Circumstances when and how the train warning horn should be used",
            "10. Reasons and methods used to conduct a running brake test",
            "11. Reasons for reducing speed during adverse weather conditions",
            "12. Locations where coasting is appropriate and reasons for coasting",
            "13. Meaning of flashing yellow signals and drivers actions",
            "14. Actions a driver can take to minimise delay to trains",
          ],
        },
        {
          id: "4.3",
          title: "Element 4.3 Stop trains",
          demonstrate: [
            "a. Identify changes in braking performance and/or signal visibility and take appropriate action to ensure stopping points are achieved",
            "b. Avoid routine cab radio or cab-to-cab telephone calls during the braking sequence",
            "c. Actively respond and where necessary, initiate the braking sequence when receiving a cautionary aspect",
            "d. Ensure all braking is carried out in a positive and smooth manner, taking account of environmental conditions",
            "e. Follow the braking instructions for each traction type avoiding heavy brake applications wherever possible",
            "f. When approaching a signal that is not normally stopped at, a positive brake application is initiated earlier and maintained for longer, reducing speed earlier",
            "g. Control train speed to ensure TPWS does not intervene",
            "h. Ensure train is brought to a stand before the signal in line with professional driving instructions, drawing closer where necessary",
            "i. Apply sufficient brakes when train is at a stand and when leaving the cab",
            "j. Set DRA in accordance with instructions",
          ],
          explain: [
            "1. Chiltern Railways professional driving requirements in relation to train braking skills and techniques",
            "2. Action if cab radio call is received during braking",
            "3. The reasons why smooth braking and limiting the movement of the power/brake controller is important",
            "4. How drivers receive information on low adhesion conditions",
            "5. Location of known areas of low adhesion",
            "6. Actions if exceptionally low adhesion is experienced",
            "7. Braking technique to use when WSP is experienced",
            "8. Low adhesion aids fitted to trains and how these are used",
            "9. Requirements and action to conduct a controlled test stop correctly",
            "10. Locations where you need to stop closer than the prescribed distance from the signal",
            "11. Actions when you are required to stop closer than the prescribed distance from the signal",
            "12. Signals that have an increased risk of SPADs, reason for the risk and actions a driver needs to take to avoid that situation",
            "13. Cab discipline requirements during braking",
            "14. Action if delayed at different signal types",
          ],
        },
        {
          id: "4.4",
          title: "Element 4.4 Work trains within stations",
          demonstrate: [
            "a. Reduce speed gradually along the length of a terminal platform ensuring the authorised speed at TPWS loops is not exceeded and a maximum speed of 5 mph is achieved two coach lengths from the authorised stopping point",
            "b. If entering platform under permissive working, reduce speed progressively along platform, taking into account the location of the other train",
            "c. Stop train smoothly at the appropriate car-stop mark or at the prescribed distance from another train or buffer stops",
            "d. On DMU services, release doors on the correct side of the train when it is safe to do so",
          ],
          explain: [
            "1. Different signal types and indications that control movements into stations",
            "2. Terminal stations on the Chiltern Railways route and the risks relating to each location",
            "3. Instructions regarding the mid platform marker boards at Birmingham Snow Hill",
            "4. Low adhesion locations relating to stations",
            "5. Stations where permissive working is permitted",
            "6. Action if TPWS intervenes on approach to buffer stops",
            "7. Reasons for ensuring a smooth stop when stopping at a station",
            "8. Action if a driver stops short or over-runs a station platform",
            "9. Action if there is an accidental collision with another vehicle or buffer stops",
            "10. Bell buzzer codes used relating to door release information to/from the Train Manager",
            "11. Action to take if doors are released on the wrong side of the train or when the train is not at a platform",
          ],
        },
        {
          id: "4.5",
          title: "Element 4.5 Change ends and handover trains in service",
          demonstrate: [
            "a. Secure cab to prevent unauthorised access when leaving, in line with train and cab security procedures",
            "b. Ensure the DRA is set when leaving or entering a driving cab",
            "c. Where turn round time allows, check general condition of train interior and for suspicious packages in line with 'HOT' protocols",
            "d. Before opening cab controls, check sufficient brakes are applied to prevent the train from moving",
            "e. Set up and/or check frontal/rear lights, destination indicators and PIS where appropriate",
            "f. Ensure information is input to ATP and other on-board systems as required for the traction type",
            "g. De-register and register GSM-R cab radio system",
            "h. Adjust driving environment to suit individual needs",
            "i. When taking over a train establish that it is the correct train for the diagram and or service",
            "j. When handing over to another driver, communicate relevant information regarding train characteristics and ensure sufficient brakes are applied to prevent the train from moving, DRA is set and master switch to neutral",
            "k. When the relieving driver or train manager is not present, promptly inform the operations control",
          ],
          explain: [
            "1. Action a driver needs to take when changing ends",
            "2. Reasons for securing driving cabs",
            "3. Information required to be entered into ATP and OTDR systems",
            "4. Action if frontal/rear lighting, destination indicator, TMS or PIS becomes defective",
            "5. Reason for setting the DRA when leaving or entering a cab",
            "6. Action if GSM-R cab radio system cannot be set up",
            "7. Action if PIS system cannot be set up",
            "8. Typical information that needs to be communicated to the relieving driver",
            "9. Action if relieving driver is not present at the designated location",
            "10. How to make contact with operations control",
            "11. Who needs to be told if the relieving driver is not present",
          ],
        },
        {
          id: "4.6",
          title: "Element 4.6 Conduct other drivers",
          demonstrate: [
            "a. Clarify and agree roles and responsibilities of each driver prior to commencing any movement",
            "b. Discuss and agree any restrictions to the operation of the train or route If being conducted",
            "c. If conductor driver does not sign the traction, identify and explain relevant train safety equipment and operational controls",
            "d. Respond promptly to instruction given, repeating messages received and if unsure stop the train in a safe manner",
            "e. Communicate and exchange all information (route risk and key characteristics etc) promptly, clearly and accurately for the safe operation of train",
            "f. Take appropriate action if driver fails to act correctly to any instruction you provide",
          ],
          explain: [
            "1. Chiltern Railways professional driving requirements relating to conducting another driver",
            "2. Issues to discuss and clarify between drivers before conducting",
            "3. Safety equipment and driving controls that need to be identified and discussed prior to commencing journey",
            "4. Action to take if a driver does not respond to instructions",
            "5. Action to take if you are unclear of an instruction given by the conductor driver",
            "6. Typical hazards relating to conducting and how to prevent them",
            "7. The circumstances where route learners are allowed to drive",
            "8. Typical route risk information that needs to be communicated during the journey",
            "9. Typical information that needs to be communicated during the journey",
            "10. Actions if the conductor driver doesn't sign the traction type",
            "11. Actions if the conductor is required to make an unusual or infrequent move",
          ],
        },
      ],
    },
    {
      id: "u5",
      title: "UNIT 5 - Respond to train faults",
      elements: [
        {
          id: "5.1",
          title: "Element 5.1 Identify and respond to train faults",
          demonstrate: [
            "a. Promptly inform signaller of the circumstances, including the likely time required to assess and rectify the fault (train movements)",
            "b. Seek advice from Operations Control/Duty Fleet Manager where fault(s) cannot be accurately identified or quickly rectified",
            "c. Use the fault guide to accurately and promptly identify and rectify train faults as appropriate",
            "d. Respond appropriately to fault indication (including TMS messages) received using fault guide, and considering service headway",
            "e. Stop train (if required) in a safe and convenient location where possible",
            "f. Block the line before inspecting or working on the outside of your train",
            "g. Decide with Signaller and Operations Control the next step to avoid undue delay to the train and other services",
            "h. Take appropriate action to rectify the problem and minimise delay",
            "i. Operate train safely, in line with operational restrictions relative to the particular problem",
            "j. Operate train from other than the leading cab in line with instructions, when required",
            "k. Record faults and actions taken accurately in defect book and fax to Operations Control",
          ],
          explain: [
            "1. Core operational restrictions that apply if the following train system/equipment becomes non operational in service",
            "2. Actions following operation of AWS or TPWS",
            "3. The key fault indications received that help in identifying the nature of the fault",
            "4. Appropriate person to contact to help with fault identification/solving",
            "5. Reasons for, and occasions when a competent person must accompany drivers",
            "6. Methods used to effectively report train faults",
          ],
        },
        {
          id: "5.2",
          title: "Element 5.2 Obtain assistance to a failed train",
          demonstrate: [
            "a. Secure failed train and report its specific location accurately to the controlling signaller",
            "b. Protect failed train in accordance with applicable rules and inform signaller when this has taken place",
            "c. Agree recovery arrangements with the signaller",
            "d. Stop assisting train at agreed location or protecting point and then conduct onto failed train",
            "e. Provide relevant information to assisting driver before entering protection and again prior to coupling",
            "f. Discuss and clarify with assisting driver the arrangements prior to future train movements",
            "g. Operate revised train formation safely and in line with operational restrictions",
          ],
          explain: [
            "1. Approved methods of contacting the signaller",
            "2. Typical items to discuss and agree with signaller prior to assistance being provided",
            "3. Trains that can assist Chiltern Railways train types",
            "4. Protection methods if assistance is required",
            "5. Actions required by traincrew to guide assisting train to the failed train",
            "6. Action required by traincrew to prepare failed train for assistance",
            "7. Information required by assisting driver prior to moving past the assistance protection limits",
            "8. Information required by assisting driver prior to coupling",
            "9. Various operational restrictions possible during assistance and maximum speeds related to each",
            "10. Typical hazards that can occur during assistance and the actions a driver needs to take to overcome them",
            "11. Reasons for keeping signaller and operations control informed of the progress",
            "12. Sidings on routes that can be used to stable failed trains",
          ],
        },
      ],
    },
    {
      id: "u6",
      title: "UNIT 6 - Respond to out of course situation",
      elements: [
        {
          id: "6.1",
          title: "Element 6.1 Operate trains during disruption and degraded working",
          demonstrate: [
            "a. Check for any altered working that could affect the train before leaving departure location",
            "b. Obtain and clarify all relevant information before working under any revised operating conditions",
            "c. Respond appropriately to temporary, emergency and weather related speed restrictions",
            "d. Pass a signal at danger with the signaller's authority",
            "e. Operate train safely and in line with instructions for temporary block working conditions",
            "f. Operate train safely and in line with instructions for single line working conditions by pilotman",
            "g. Operate train safely in adverse and severe weather conditions and exceptional rail head conditions",
            "h. Operate trains safely in the wrong direction where signals are not provided",
            "i. Operate trains under bi-directional working",
            "j. Operate trains from other than the leading cab in line with instructions when required",
          ],
          explain: [
            "1. Core requirements for operating a train safely under temporary block working or over a single line worked by a Pilotman",
            "2. Situations where a signal can be passed at danger with authority",
            "3. Actions and considerations if required to work to and from a point of obstruction",
            "4. Arrangements for notifying a driver of temporary, emergency and heat speed restrictions",
            "5. Locations where bi-directional working is possible and working arrangements for operating",
            "6. Actions and consideration when requested to make a wrong direction move not controlled by fixed signals or start a train when in advance of a controlled signal",
            "7. Methods used to dispatch a train when there is a failure of the bell/buzzer equipment",
            "8. Methods of dispatching trains safely if a starting signal and/or associated equipment has failed",
            "9. Dispatch from a station if equipment provided for train dispatch purposes is defective or view is unsatisfactory",
            "10. Actions of a driver during adverse and severe weather conditions and the reporting process for exceptional railhead conditions",
            "11. Reasons for placing brake to emergency in low adhesion conditions and action when performing a controlled test stop",
            "12. Actions and considerations when approaching a failed level crossing",
            "13. Method used to advise a driver that altered methods of working are in operation",
            "14. Operational situations where driving from other than the leading cab is required",
            "15. Failure of a level crossing and the responsibilities of a 'Crossing Attendant'",
          ],
        },
        {
          id: "6.2",
          title: "Element 6.2 Respond to on-train and line side hazards",
          demonstrate: [
            "a. Report immediately to signaller, using approved method, any event likely to affect train or individual safety or security",
            "b. Report immediately to the signaller any unauthorised person or large animal within the boundary fence",
            "c. Examine the line in accordance with requirements and details provided by the signaller",
            "d. Report immediately to the signaller any line side fire",
            "e. Take appropriate action if a signal reversion or irregularity is encountered, or a signal is passed at danger without authority",
            "f. Respond appropriately to an unsolicited brake application via the AWS or TPWS",
            "g. Report immediately any suspicious objects, person or trespasser, using approved method",
            "h. Inform signaller immediately if a broken rail, displaced conductor rail or other track defect is suspected or identified",
            "i. Take additional action when involved or notified of a dangerous goods incident",
          ],
          explain: [
            "1. Core requirements for dealing with lineside fires",
            "2. Typical indications that may prompt a driver to report a suspected broken rail or track defect",
            "3. Action to be taken if TPWS is activated or intervenes",
            "4. Actions when a signal irregularity or imperfectly displayed signal is observed",
            "5. Actions if wrong route is observed or taken at a signal",
            "6. A signal is passed at danger without authority",
            "7. Train overruns a station platform or fails to call",
            "8. Actions if required to assist during a dangerous goods incident",
            "9. Hot axle box occurs on a locomotive hauled train/diesel multiple unit (especially class 172)",
            "10. A passenger is taken ill",
            "11. A suspect package is reported",
            "12. Actions if a trespasser or suspicious behaviour is observed",
            "13. Failure or concerns with temporary or emergency speed restriction warnings",
            "14. Flooding of the line or build up of snow occurs",
            "15. Requested to examine the line",
          ],
        },
        {
          id: "6.3",
          title: "Element 6.3 Respond to emergency situations",
          demonstrate: [
            "a. Apply emergency brake (if required) and take appropriate action if actual or likely obstructions of the line are observed",
            "b. Obtain an isolation of traction current supply using approved methods (AC or DC)",
            "c. Carry out necessary protection arrangements, including warning other drivers, according to the specific situation",
            "d. Report immediately to the signaller any on-train fire, fire on other trains or out-of-gauge loads",
            "e. Deal with any train fire(s) promptly using approved methods and appropriate equipment, switching off air con or Head End Power supply at once (where fitted)",
            "f. Follow correct safety of the line procedures after colliding with any obstruction",
            "g. Respond appropriately to exploding detonators on the line",
            "h. Actions when your train becomes divided in service",
            "i. Respond appropriately to the operation of a pass comm",
          ],
          explain: [
            "1. Approved methods used to report emergency situations",
            "2. Circumstances that warrant initiating an emergency call on the cab radio",
            "3. Core requirements for emergency protection of the line",
            "4. Core requirements for dealing with an emergency isolation of AC or DC traction supply (including 3rd and 4th rail)",
            "5. Actions if a fire occurs on your train",
            "6. Actions if collision with an obstruction occurs",
            "7. Actions if your train becomes involved in a potential suicide or fatality",
            "8. Actions when your train becomes divided in service",
            "9. Actions when one or more detonators are exploded",
            "10. When it would be appropriate to respond to the operation of passenger communication equipment",
            "11. Actions if passenger communication equipment has been operated, including different traction types",
          ],
        },
        {
          id: "6.4",
          title: "Element 6.4 Evacuate trains",
          demonstrate: [
            "a. If possible, stop or move the train to an appropriate location for the evacuation",
            "b. Use the cab to cab and or public address system to inform train manager/customers to remain in the train until it is safe to evacuate",
            "c. Take appropriate action to stop all trains and protect all lines",
            "d. Where necessary arrange for an immediate isolation of AC or DC traction current, using short circuiting devices on London Underground DC electrified lines",
            "e. Move customers to an appropriate position within the train, or safe position on the lineside until alternative arrangements can be made",
            "f. Confirm appropriate protection is in place",
            "g. Discuss with signaller, operations control and where provided the train manager the most appropriate way to evacuate customers from the train and their subsequent movement from site",
            "h. Obtain necessary authority for a controlled evacuation through signaller, liaising with operations control where appropriate",
            "i. Obtain assistance as necessary from other rail staff and customers and coordinate their actions",
            "j. Ensure a clear understanding is reached between all involved",
            "k. Select the most appropriate door to use to evacuate through then position and secure ladders for the safe movement of people",
            "l. Inform customers the safest way to evacuate the train and walk safely on or near the line",
            "m. Assist customers from train using a safe manner, utilising rail staff and or customers as necessary",
            "n. Monitor the progress and movement of people to a position of safety",
          ],
          explain: [
            "1. Suitable places to stop a train to undertake a controlled evacuation and places to avoid, if possible",
            "2. Reasons why an uncontrolled evacuation may take place and immediate action to take",
            "3. Actions that a driver and or train manager can take to prevent an uncontrolled evacuation",
            "4. Occasions AC traction current may need isolating",
            "5. Occasions DC traction current may need isolating",
            "6. Occasions short circuiting bars will need to be used",
            "7. Typical reasons for a controlled evacuation",
            "8. Action in response to a fire alarm and how to deal with a fire on a train (small and large)",
            "9. Location of ladders provided for use during evacuations",
            "10. Criteria for selecting staff/customers to assist during an evacuation",
            "11. Information customers must be given prior to evacuation",
            "12. The most suitable door(s) to use to evacuate customers from",
            "13. Actions required to control customer safety during an evacuation",
            "14. Actions required to deal with mobility impaired, infirm, elderly or injured customers",
          ],
        },
      ],
    },
    {
      id: "u7",
      title: "UNIT 7 - Provide effective customer service",
      elements: [
        {
          id: "7.1",
          title: "Element 7.1 Assist customers",
          demonstrate: [
            "a. Treat customers in a polite, respectful and friendly manner",
            "b. When working DOO (P) services use 'in cab' communication equipment to relay appropriate information to customers in accordance with requirements",
            "c. Communicate information to the train manager or customers only when it is safe to do so",
            "d. Relay information in an accurate, clear and concise manner",
            "e. Inform train manager/customers promptly of any service delay/alteration or special information notices",
          ],
          explain: [
            "1. How to obtain current train service information for Chiltern Railways services",
            "2. Staff that are likely to be able to provide more detailed customer related information",
            "3. How voice tone and body language can defuse/escalate potential conflict situations",
            "4. Action to take if a person behaves in an aggressive or anti social manner",
            "5. When it is safe to use the 'in cab' communication equipment",
            "6. Action prior to making any announcements to customers",
            "7. Situations where it is important to communicate with customers",
            "8. Key locations where train service information is to be communicated to customers",
            "9. Alternative methods of communicating with customers",
            "10. Actions if assisting a customer would cause delay",
            "11. How to deal with customers with disabilities or special needs",
            "12. Stations with disabled access/egress on Chiltern Railways routes",
          ],
        },
      ],
    },
    {
      id: "u8",
      title: "UNIT 8 - London Underground (LU) infrastructure",
      elements: [
        {
          id: "8.1",
          title: "Element 8.1 Operate over London Underground (LU) infrastructure",
          demonstrate: [
            "a. Monitor correct operation of trip cock testing equipment",
            "b. Actions if you are authorised to pass a signal at danger",
            "c. Actions if you pass a signal at danger under your own authority",
            "d. Single line working (train locked in) and single line working (train not locked in) instructions",
            "e. How to set a train back by up to one car without changing ends after a train has overrun a platform stopping mark, platform or a signal at danger",
            "f. Actions if your train is obstructing an adjacent track due to a derailment",
            "g. Wrong direction movements",
            "h. Point to point working including maximum permitted speeds",
            "i. How to isolate traction current on DC lines",
          ],
          explain: [
            "1. Who are you in contact with when using the GSM-R radio",
            "2. The meaning of LUL Semi-automatic, automatic and fog signals and how they are identified and controlled",
            "3. Who can authorise a driver to pass a signal at danger",
            "4. Actions if you pass a signal at danger with authority",
            "5. Actions if you pass a signal at danger under your own authority, including if a train is found in the section ahead",
            "6. What instructions apply to the operation and testing of the tripcock and trainstop apparatus",
            "7. What is a possible meaning of red and green signal aspects displayed simultaneously",
            "8. When detonator protection would be required and the distances involved",
            "9. What would you do if your train exploded one or more detonators",
            "10. How to request traction current to be switched off on London Underground lines",
            "11. Where are short circuiting devices located on the train and what instructions apply to their use",
            "12. When you are permitted to sound the train horn at whistle boards",
            "13. Maximum permitted speeds during single line working, working in the wrong direction and point to point working",
            "14. What is the meaning of Code Amber or Code Red messages",
            "15. Actions if a Rail Gap Indicator is illuminated",
          ],
        },
      ],
    },
    {
      id: "u9",
      title: "UNIT 9 - Railway safety and security",
      elements: [
        {
          id: "9.1",
          title: "Element 9.1 Maintain a secure railway environment",
          demonstrate: [
            "a. Explain the process for maintaining railway safety and security",
            "b. Take the appropriate action to report any risk to safety and/or security",
            "c. Deal with trespass on Network Infrastructure regarding communication protocols",
            "d. Demonstrate how they are able to challenge a person they believe to be acting suspiciously on premises",
            "e. Identify and explain unsafe conditions and how to report them",
            "f. Take appropriate action to have hazardous items removed safely (e.g. sharps, drug paraphernalia)",
          ],
          explain: [
            "1. Process for maintaining railway safety and security",
            "2. How to report any risk to safety and/or security",
            "3. Communication protocols for dealing with trespass",
            "4. How to challenge a person acting suspiciously",
            "5. Unsafe conditions and how to report them",
            "6. Action to have hazardous items removed safely",
          ],
        },
      ],
    },
    {
      id: "u10",
      title: "UNIT 10 - Dangerous goods",
      elements: [
        {
          id: "10.1",
          title: "Element 10.1 Dangerous Goods knowledge",
          demonstrate: [
            "a. Apply dangerous goods knowledge when working trains conveying dangerous goods",
          ],
          explain: [
            "1. How to report a train accident to the Signaller when working a train conveying dangerous goods",
            "2. Additional safety precautions if involved in a train accident involving dangerous goods",
            "3. Purpose, structure and contents of the Handling and Carriage of Dangerous Goods Manual (e.g. GERT8000 TW4 Section 9)",
            "4. The term 'dangerous goods incident'",
            "5. Classification of dangerous goods, including where UN numbers are found",
            "6. Where to find and the meaning of the dangerous goods emergency action code",
            "7. Dangerous goods separation requirements and where this can be found",
            "8. Regulations when loading dangerous goods onto vehicles with inoperative brakes or piped only vehicles",
            "9. Train documentation and the requirement to cross check against the loads conveyed on the train",
            "10. Information contained on the hazard warning placard",
            "11. Packing groups I, II and III",
            "12. Requirements of additional PPE for dangerous goods",
            "13. Actions if required to assist during a dangerous goods incident (from Unit 6.2)",
          ],
        },
      ],
    },
  ],
};
